interface AudioOptions {
  fftSize?: number
  smoothing?: number
  throttle?: number
  minDecibels?: number
  maxDecibels?: number
  enablePulse?: boolean
  enableFrequency?: boolean
  enableBars?: boolean

  barOptions?: AudioBarOptions
}

interface AudioBarOptions {
  enable?: boolean
  barCount: number
  minHeight: number
  maxHeight: number
}

export function useAudioAnalyzer(
  stream: Ref<MediaStream | undefined>,
  options: AudioOptions,
) {
  const {
    fftSize = 32,
    smoothing = 0.8,
    throttle = 50,
    minDecibels = -100,
    maxDecibels = -50,
    barOptions = {
      enable: true,
      barCount: 6,
      minHeight: 5,
      maxHeight: 30,
    },
    enableBars = false,
    enablePulse = false,
    enableFrequency = false,
  } = options

  // Audio analysis state
  const audioContext = ref<AudioContext | null>(null)
  const analyser = ref<AnalyserNode | null>(null)
  const frequency = ref<Uint8Array>(new Uint8Array(0))
  const pulse = ref(0)
  const bars = ref<number[]>([])

  // Calculate pulse with throttling
  const updatePulse = useThrottleFn(() => {
    if (!analyser.value)
      return

    const data = new Uint8Array(analyser.value.frequencyBinCount)
    analyser.value.getByteFrequencyData(data)

    const sum = data.reduce((a, b) => a + b, 0)
    const avg = sum / data.length
    pulse.value = Math.round(Math.min(avg / 255, 1) * 100) // Convert to percentage & round
  }, throttle)

  // Calculate bars with throttling
  const updateBars = useThrottleFn(() => {
    if (!analyser.value)
      return

    const data = new Uint8Array(analyser.value.frequencyBinCount)
    analyser.value.getByteFrequencyData(data)

    const binCount = data.length
    const binsPerBar = Math.floor(binCount / barOptions.barCount)

    const newHeights: number[] = Array.from<number>({ length: barOptions.barCount }).fill(0)

    for (let i = 0; i < barOptions.barCount; i++) {
      let sum = 0
      const startBin = i * binsPerBar

      for (let j = 0; j < binsPerBar; j++) {
        sum += data[startBin + j] ?? 0
      }

      const average = sum / binsPerBar
      const heightPercent = average / 255
      const height = barOptions.minHeight + (heightPercent * (barOptions.maxHeight - barOptions.minHeight))

      // Calculate distance from center
      const distanceFromCenter = Math.abs(i - Math.floor(barOptions.barCount / 2))
      const adjustedHeight = height * (1 - distanceFromCenter / (barOptions.barCount / 2))

      newHeights[i] = Math.max(barOptions.minHeight, adjustedHeight)
    }

    bars.value = newHeights
  }, throttle)

  const updateFrequency = useThrottleFn(() => {
    if (!analyser.value)
      return

    const data = new Uint8Array(analyser.value.frequencyBinCount)
    analyser.value.getByteFrequencyData(data)
    frequency.value = data
  }, throttle)

  // Setup audio processing when stream becomes available
  watch(stream, (mediaStream) => {
    if (!mediaStream)
      return

    audioContext.value = new AudioContext()
    analyser.value = audioContext.value.createAnalyser()

    analyser.value.fftSize = fftSize
    analyser.value.smoothingTimeConstant = smoothing
    analyser.value.minDecibels = minDecibels
    analyser.value.maxDecibels = maxDecibels

    const source = audioContext.value.createMediaStreamSource(mediaStream)
    source.connect(analyser.value)

    // Ensure audio context is resumed to trigger analysis
    audioContext.value.resume().catch((error) => {
      console.error('Error resuming AudioContext:', error)
    })
  }, {
    immediate: true,
  })

  // Animation frame loop using VueUse's raf utility
  const { resume: startRaf } = useRafFn(() => {
    if (enablePulse)
      updatePulse()

    if (enableBars)
      updateBars()

    if (enableFrequency)
      updateFrequency()
  }, { immediate: true })

  // Auto cleanup
  onScopeDispose(() => {
    audioContext.value?.close()
    pulse.value = 0
  })

  // Ensure RAF is started after audio context is set up
  watch(audioContext, (context) => {
    if (context) {
      startRaf()
    }
  })

  return {
    /**
     * Pulse value (0-100)
     */
    pulse,
    /**
     * Animation bars array
     */
    bars,
    /**
     * Raw frequency data array
     */
    frequency,
    /**
     * Audio context state
     */
    audioContext: audioContext.value,
    /**
     * Whether audio is currently being processed
     */
    isListening: !!stream.value,
  }
}
